// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/**
 * The call controller. Wraps trystero (nostr strategy) into a small,
 * call-shaped API. trystero handles peer discovery over public nostr relays and
 * encrypts the signalling handshake with the room passphrase, so the relay only
 * ever sees ciphertext. Voice itself is full-mesh WebRTC — direct
 * browser-to-browser, DTLS-SRTP encrypted, with no server in the path.
 */
import { joinRoom, selfId } from 'trystero/nostr';
import type { Room, BaseRoomConfig, JsonValue, MessageAction } from '@trystero-p2p/core';
import type { Presence } from './types.ts';

const APP_ID = 'talkwell-v1-benrichardson-dev';

export interface ChatMsg {
  text: string;
}

export interface CallHandlers {
  onPeerJoin: (peerId: string) => void;
  onPeerLeave: (peerId: string) => void;
  onStream: (peerId: string, stream: MediaStream) => void;
  onPresence: (peerId: string, presence: Presence) => void;
  onChat: (peerId: string, msg: ChatMsg) => void;
  onError: (message: string) => void;
}

function swallow(promises: Promise<void>[]): void {
  for (const p of promises) p.catch(() => undefined);
}

export class CallController {
  readonly selfId = selfId;
  private room: Room | null = null;
  private localStream: MediaStream | null = null;
  private streamedTo = new Set<string>();
  private presAction: MessageAction<JsonValue> | null = null;
  private chatAction: MessageAction<JsonValue> | null = null;
  private myPresence: Presence;

  constructor(
    private room_id: string,
    private pass: string,
    presence: Presence,
    private handlers: CallHandlers,
  ) {
    this.myPresence = presence;
  }

  /** Join the room. `localStream` may be null for a listen-only (mic-denied) join. */
  join(localStream: MediaStream | null): void {
    this.localStream = localStream;
    const config: BaseRoomConfig = { appId: APP_ID };
    if (this.pass) config.password = this.pass;

    let room: Room;
    try {
      room = joinRoom(config, this.room_id);
    } catch (e) {
      this.handlers.onError(`Could not join: ${(e as Error).message}`);
      return;
    }
    this.room = room;

    this.presAction = room.makeAction<JsonValue>('pres');
    this.chatAction = room.makeAction<JsonValue>('chat');
    this.presAction.onMessage = (data, ctx) => this.handlers.onPresence(ctx.peerId, data as unknown as Presence);
    this.chatAction.onMessage = (data, ctx) => this.handlers.onChat(ctx.peerId, data as unknown as ChatMsg);

    room.onPeerJoin = (peerId) => {
      this.handlers.onPeerJoin(peerId);
      this.attachStreamToPeer(peerId);
      // Introduce ourselves to the newcomer.
      this.presAction?.send(this.myPresence as unknown as JsonValue, { target: peerId }).catch(() => undefined);
    };

    room.onPeerLeave = (peerId) => {
      this.streamedTo.delete(peerId);
      this.handlers.onPeerLeave(peerId);
    };

    room.onPeerStream = (stream, peerId) => {
      this.handlers.onStream(peerId, stream);
    };
  }

  private attachStreamToPeer(peerId: string): void {
    if (!this.room || !this.localStream) return;
    if (this.streamedTo.has(peerId)) return;
    try {
      swallow(this.room.addStream(this.localStream, { target: peerId }));
      this.streamedTo.add(peerId);
    } catch {
      /* peer gone before we could add */
    }
  }

  /** Announce our current transmit/record/mode state to everyone. */
  broadcastPresence(presence: Presence): void {
    this.myPresence = presence;
    this.presAction?.send(presence as unknown as JsonValue).catch(() => undefined);
  }

  sendMessage(text: string): void {
    this.chatAction?.send({ text } as unknown as JsonValue).catch(() => undefined);
  }

  /** Promote a listen-only session to sending: set a local stream and push it to all peers. */
  setLocalStream(stream: MediaStream): void {
    if (this.localStream && this.localStream !== stream && this.room) {
      try {
        this.room.removeStream(this.localStream);
      } catch {
        /* already removed */
      }
    }
    this.localStream = stream;
    this.streamedTo.clear();
    if (this.room) for (const peerId of Object.keys(this.room.getPeers())) this.attachStreamToPeer(peerId);
  }

  getPeerIds(): string[] {
    return this.room ? Object.keys(this.room.getPeers()) : [];
  }

  leave(): void {
    if (!this.room) return;
    try {
      void this.room.leave();
    } catch {
      /* already left */
    }
    this.room = null;
    this.streamedTo.clear();
  }
}
